<template>
  <slot :activatorProps="activatorProps" :headers="visibleHeaders" />

  <v-dialog v-model="dialog" width="420">
    <template #activator="{ props }">
      <div v-bind="bindActivator(props)" />
    </template>

    <dialog-template
      :title="title"
      dialog-width="400px"
      hide-action-buttons
      @close="closeDialog">
      <div>
        <v-chip class="mb-4" color="amber-lighten-1">
          <span class="font-weight-bold text-deep-orange-darken-4">
            {{ selected.length }} з {{ headers.length }} Увімкнено стовпчиків
          </span>
        </v-chip>

        <div style="max-height: 250px; overflow: auto">
          <div
            v-for="column in headers"
            :key="column.key"
            class="d-flex align-center py-2"
            style="border-bottom: 1px solid #f5f5f8">
            <v-checkbox
              v-model="selected"
              :disabled="column.disabled"
              :value="column.key"
              color="blue-darken-3"
              hide-details>
              <template #label>
                <div class="d-flex align-center ga-1">
                  <span
                    :class="{ 'text-disabled': column.disabled }"
                    class="ml-2">
                    {{ column.title }}
                  </span>
                  <v-icon v-if="column.disabled" size="small">
                    mdi-lock-outline
                  </v-icon>
                </div>
              </template>
            </v-checkbox>
          </div>
        </div>
      </div>

      <div class="d-flex align-center justify-space-between mt-5">
        <v-btn
          :color="isHovering ? 'black' : 'grey-darken-1'"
          class="pa-0"
          density="compact"
          variant="text"
          @click="reset"
          @mouseenter="isHovering = true"
          @mouseleave="isHovering = false">
          <span class="font-weight-medium"> Скинути до стандарту </span>
        </v-btn>

        <v-btn color="blue-darken-3" @click="submit"> Готово </v-btn>
      </div>
    </dialog-template>
  </v-dialog>
</template>

<script lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import DialogTemplate from '@/dialog/DialogTemplate.vue'
import { useStore } from '@/plugins'

export default {
  name: 'TableColumnPicker',
  components: { DialogTemplate },
  props: {
    headers: {
      type: Array as () => any[],
      required: true,
    },
    storageKey: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: 'Налаштування стовпців',
    },
  },
  setup(props) {
    const store = useStore()
    const dialog = ref(false)

    const selected = ref<string[]>([])
    const appliedSelected = ref<string[]>([])

    const activatorProps = ref({})
    const isHovering = ref(false)

    const bindActivator = (props: any) => {
      activatorProps.value = props

      return {}
    }

    const requiredKeys = computed(() =>
      props.headers.filter(h => h.disabled).map(h => h.key)
    )

    const userId = computed(
      () =>
        store.state.user.userData?.id ||
        store.state.auth.authUser?.id ||
        localStorage.getItem('userId')
    )

    const currentStorageKey = computed(() => {
      if (!userId.value) {
        return props.storageKey
      }

      return `table-column-picker:${userId.value}:${props.storageKey}`
    })

    const normalizeSelected = (keys: string[]) => {
      const headerKeys = props.headers.map(h => h.key)

      return Array.from(
        new Set([
          ...keys.filter(key => headerKeys.includes(key)),
          ...requiredKeys.value,
        ])
      )
    }

    const getSavedSelected = () => {
      const saved = localStorage.getItem(currentStorageKey.value)

      if (!saved) {
        return props.headers.map(h => h.key)
      }

      try {
        const parsed = JSON.parse(saved)

        return Array.isArray(parsed) ? parsed : props.headers.map(h => h.key)
      } catch {
        localStorage.removeItem(currentStorageKey.value)

        return props.headers.map(h => h.key)
      }
    }

    const applySavedSelected = () => {
      const initial = normalizeSelected(getSavedSelected())

      selected.value = [...initial]
      appliedSelected.value = [...initial]
    }

    onMounted(applySavedSelected)

    const visibleHeaders = computed(() => {
      return props.headers.filter(h => appliedSelected.value.includes(h.key))
    })

    const closeDialog = () => {
      selected.value = [...appliedSelected.value]
      dialog.value = false
    }

    const submit = () => {
      const fixed = Array.from(
        new Set([...selected.value, ...requiredKeys.value])
      )

      selected.value = fixed
      appliedSelected.value = fixed

      localStorage.setItem(currentStorageKey.value, JSON.stringify(fixed))

      dialog.value = false
    }

    const reset = () => {
      selected.value = props.headers.map(h => h.key)
    }

    watch(
      () => props.headers,
      newHeaders => {
        const keys = newHeaders.map(h => h.key)

        appliedSelected.value = appliedSelected.value.filter(k =>
          keys.includes(k)
        )

        appliedSelected.value = Array.from(
          new Set([...appliedSelected.value, ...requiredKeys.value])
        )
      }
    )

    watch(currentStorageKey, applySavedSelected)

    return {
      dialog,
      selected,
      visibleHeaders,
      closeDialog,
      submit,
      reset,
      activatorProps,
      bindActivator,
      isHovering,
    }
  },
}
</script>

<style lang="scss" scoped>
:deep(.v-checkbox .v-selection-control) {
  min-height: 25px !important;
}
</style>
